<template>
  <div class="fts-settings-page">
    <header class="fts-settings-head">
      <div>
        <h1>Plan</h1>
        <p>
          Your plan sets capacity. Actor seats and AI credits are bought separately, so you only pay
          for what you use.
        </p>
      </div>
    </header>

    <!-- States 8 (billing not switched on here) replaces the hard-coded payment links
         the old dialog fell back to. -->
    <section class="fts-settings-card" v-if="fallback === 404">
      <h2>Your plan is arranged with Inistate</h2>
      <p class="fts-settings-card-sub">
        Plans are not sold online in this environment. Talk to us to change the plan; usage, people
        and security settings all work here as usual.
      </p>
      <div>
        <a class="fts-settings-btn" href="mailto:support@inistate.com">Talk to us</a>
      </div>
    </section>

    <section class="fts-settings-card" v-else-if="fallback === 403">
      <h2>Only a workspace admin can change the plan</h2>
      <p class="fts-settings-card-sub">
        Ask a workspace admin if you need a change to the plan, seats or AI credits.
      </p>
    </section>

    <section class="fts-settings-card" v-else-if="fallback">
      <h2>Plan information is not available right now</h2>
      <p class="fts-settings-card-sub">Please try again in a moment.</p>
    </section>

    <!-- A workspace billed by its vendor sees no price ladder: its plan is arranged with the
         vendor, and none of these plans can be bought for it (pricing spec §12.3). -->
    <settings-plan-compare
      v-if="!fallback && summary && !isManual"
      :summary="summary"
      :subscribe="needsCheckout"
      @subscribe="subscribe"
    />

    <settings-plan-estimate
      v-if="!fallback && summary && !isManual"
      :summary="summary"
      :subscribe="needsCheckout"
      @review="onReview"
    />

    <settings-plan-review
      v-if="!fallback && preview"
      :preview="preview"
      :busy="busy"
      @confirm="confirmChange"
      @cancel="preview = null"
    />

    <!-- The shipped Plan &amp; Credits page, mounted here unchanged apart from its upgrade
         block, which the comparison above replaces. P1 moves credits and history out to
         their own pages. -->
    <fts-workspace-billing
      ref="billing"
      v-show="!fallback"
      :workspace-id="workspaceId"
      hide-upgrade
      @fallback="onFallback"
      @summary="onSummary"
    />
  </div>
</template>

<script>
/**
 * Settings › Plan (alpha, P0 slice).
 *
 * P0 moves the existing "Plan & Credits" page into the settings area as-is and gives
 * its 404/403 states a real home — that is what retires the two hard-coded
 * buy.stripe.com links the old dialog showed when billing was switched off.
 */
import FTSWorkspaceBilling from "../../design/FTSWorkspaceBilling";
import SettingsPlanCompare from "./SettingsPlanCompare";
import SettingsPlanEstimate from "./SettingsPlanEstimate";
import SettingsPlanReview from "./SettingsPlanReview";
import { commonStore } from "../../store/commonStore";

export default {
  name: "SettingsPlan",
  components: {
    "fts-workspace-billing": FTSWorkspaceBilling,
    "settings-plan-compare": SettingsPlanCompare,
    "settings-plan-estimate": SettingsPlanEstimate,
    "settings-plan-review": SettingsPlanReview,
  },
  props: {
    workspaceId: [String, Number],
  },
  data() {
    return {
      fallback: null,
      summary: null,
      busy: false,
      // The in-app review of a plan change (SS06197): Stripe's preview of the change, and the
      // choice it was made for, which confirming sends again.
      preview: null,
      previewChoice: null,
    };
  },
  computed: {
    // A workspace billed by a partner buys nothing here: the estimate would offer a
    // change it cannot make (checkout and portal both answer 409 dealer_billed).
    isManual() {
      return this.summary && this.summary.source === "manual";
    },
    /**
     * No live subscription to change: never bought, or cancelled. The portal can only change a
     * subscription that exists (before the first purchase it answers 409 no_customer, SS06203),
     * so the choice goes to Stripe Checkout. past_due still holds its subscription: it only
     * needs paying, which is the portal's job.
     */
    needsCheckout() {
      const status = this.summary && this.summary.status;
      return status !== "active" && status !== "past_due";
    },
  },
  methods: {
    onFallback(status) {
      this.fallback = status || "error";
    },
    // The billing page already fetches the summary; the comparison rides on that one
    // call rather than asking for it again.
    onSummary(summary) {
      this.summary = summary;
    },
    /**
     * Where a chosen plan goes (SS06197): Checkout without a live subscription; the billing portal's
     * review-and-confirm for a subscription that is the plan alone; the in-app review once Pro seats
     * are billed as their own line, which the portal cannot change. The server says which
     * (summary.planChange). Seats are not bought here: they are billed as they are assigned.
     */
    onReview(choice) {
      if (this.needsCheckout) return this.subscribe(choice);
      const planKey = choice && choice.planKey;
      const interval = (choice && choice.interval) || "month";
      if (planKey === this.summary.planKey && interval === (this.summary.interval || "month")) {
        this.$toasted.info("Seats are billed as you assign them under People & seats; the plan stays as it is.");
        return undefined;
      }
      return this.summary.planChange === "in_app"
        ? this.previewChange(choice)
        : this.openPortal(choice);
    },
    /**
     * W-9: the estimate is ours, the money is Stripe's. A change opens the billing portal on the
     * chosen plan's review-and-confirm page, which shows the exact amount -- including any
     * proration -- before charging. A subscription that has gained a seat line since the page
     * loaded answers 409 review_in_app, and the change is reviewed here instead.
     */
    async openPortal(choice) {
      if (this.busy) return;
      this.busy = true;
      let inApp = false;
      try {
        const res = await commonStore.state.client.post("/api/billing/portal", {
          WorkspaceId: this.workspaceId,
          Flow: "subscription_update_confirm",
          PlanKey: choice && choice.planKey,
          Interval: (choice && choice.interval) || "month",
        });
        const url = res && res.data && res.data.url;
        if (url) {
          window.location.href = url;
        } else {
          this.$toasted.global.error({}, "The billing portal did not return a link.");
        }
      } catch (err) {
        const data = err && err.response && err.response.data;
        inApp = data && data.error === "review_in_app";
        if (!inApp) this.$toasted.global.error(err, (data && data.message) || "Could not open the billing portal.");
      } finally {
        this.busy = false;
      }
      if (inApp) await this.previewChange(choice);
    },
    /** Stripe's preview of the change, shown for review; nothing changes yet. */
    async previewChange(choice) {
      if (this.busy) return;
      this.busy = true;
      try {
        const res = await commonStore.state.client.post("/api/billing/plan-change/preview", this.changeBody(choice));
        this.preview = res && res.data;
        this.previewChoice = choice;
      } catch (err) {
        const data = err && err.response && err.response.data;
        this.$toasted.global.error(err, (data && data.message) || "Could not price the change.");
      } finally {
        this.busy = false;
      }
    },
    /** Makes the change the review showed; the page reloads the plan once it is done. */
    async confirmChange() {
      if (this.busy || !this.previewChoice) return;
      this.busy = true;
      try {
        await commonStore.state.client.post("/api/billing/plan-change", this.changeBody(this.previewChoice));
        this.preview = null;
        this.previewChoice = null;
        this.$toasted.success("Plan changed. Stripe sends the invoice for the difference.");
        const billing = this.$refs.billing;
        if (billing && typeof billing.fetchSummary === "function") billing.fetchSummary();
      } catch (err) {
        const data = err && err.response && err.response.data;
        this.$toasted.global.error(err, (data && data.message) || "Could not change the plan.");
      } finally {
        this.busy = false;
      }
    },
    changeBody(choice) {
      return {
        WorkspaceId: Number(this.workspaceId),
        PlanKey: choice && choice.planKey,
        Interval: (choice && choice.interval) || "month",
      };
    },
    /** A first (or renewed) subscription: Stripe Checkout for the plan and period picked. */
    subscribe(choice) {
      return this.redirect(
        "/api/billing/checkout",
        {
          WorkspaceId: Number(this.workspaceId),
          PlanKey: choice && choice.planKey,
          Interval: (choice && choice.interval) || "month",
        },
        "Stripe did not return a checkout link.",
        "Could not open Stripe Checkout.",
      );
    },
    async redirect(path, body, noLink, failed) {
      if (this.busy) return;
      this.busy = true;
      try {
        const res = await commonStore.state.client.post(path, body);
        const url = res && res.data && res.data.url;
        if (url) {
          window.location.href = url;
        } else {
          this.$toasted.global.error({}, noLink);
        }
      } catch (err) {
        const data = err && err.response && err.response.data;
        this.$toasted.global.error(err, (data && data.message) || failed);
      } finally {
        this.busy = false;
      }
    },
  },
  watch: {
    workspaceId() {
      this.fallback = null;
      this.summary = null;
      this.preview = null;
      this.previewChoice = null;
    },
  },
};
</script>
